import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Sincronizza un pezzo di stato UI (tab attiva, filtro, query, periodo) con la
 * query-string dell'URL. Così le viste diventano bookmarkabili/condivisibili e
 * tornando dal dettaglio si ritrovano i filtri (non si azzerano).
 *
 *   const [tab, setTab] = useUrlState('tab', 'fatture');
 *   const [q, setQ]     = useUrlState('q', '', { replace: true }); // no history spam
 *
 * Quando il valore == default, il parametro viene RIMOSSO dall'URL (URL puliti).
 * `replace: true` non impila history entry (ideale per search/filtri a digitazione).
 */
export function useUrlState<T extends string = string>(
  key: string,
  defaultValue: T,
  opts: { replace?: boolean } = {},
): [T, (next: T) => void] {
  const [params, setParams] = useSearchParams();
  const value = (params.get(key) as T | null) ?? defaultValue;

  const setValue = useCallback(
    (next: T) => {
      setParams(
        (prev) => {
          const sp = new URLSearchParams(prev);
          if (next === defaultValue || next === '' || next == null) sp.delete(key);
          else sp.set(key, next);
          return sp;
        },
        { replace: opts.replace ?? false },
      );
    },
    [key, defaultValue, opts.replace, setParams],
  );

  return [value, setValue];
}
